<!--
  DataCard.vue
  ============
  Composant carte de données avec en-tête et contenu.
  
  Fonctionnalités :
  - En-tête avec titre et actions optionnelles
  - Contenu personnalisable via slots
  - Styles cohérents avec le design system
  - Support des icônes et badges
  
  Utilisé pour afficher des informations structurées.
-->
<template>
  <div 
    :class="[
      'bg-white rounded-xl shadow-sm border border-gray-200 p-6 transition-all duration-300',
      hoverable && 'hover:shadow-lg hover:border-gray-300 hover:transform hover:-translate-y-1',
      clickable && 'cursor-pointer',
      className
    ]"
    @click="clickable && $emit('click')"
  >
    <!-- Header (icon + title on the left, actions on the right) -->
    <div v-if="title || icon || $slots['header-actions'] || $slots['header-toggle']" :class="['flex flex-wrap items-center justify-between gap-2 mb-4', $slots['header-toggle'] && 'max-[600px]:flex-nowrap']">
      <div class="flex flex-1 items-center gap-3 min-w-0">
        <div v-if="icon" :class="[
          'w-10 h-10 shrink-0 rounded-full text-center flex items-center justify-center',
          iconBgColor || 'bg-blue-100'
        ]">
          <i :class="[icon, iconColor || 'text-blue-600']"></i>
        </div>
        <div v-if="title" class="flex-1 min-w-0">
          <div :class="$slots['title-after'] && 'flex items-center gap-2 max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-0'">
            <h3 class="text-lg font-semibold text-black m-0 truncate text-left max-[600px]:w-full max-[600px]:whitespace-normal max-[600px]:overflow-visible">{{ title }}</h3>
            <slot name="title-after" />
          </div>
          <p v-if="subtitle" class="text-sm text-black">{{ subtitle }}</p>
        </div>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <slot name="header-toggle" />
        <div class="hidden md:contents">
          <slot name="header-actions" />
        </div>
      </div>
    </div>
    
    <!-- Content -->
    <div class="space-y-4">
      <slot />
    </div>
    
    <!-- Mobile: fixed 底部保存栏 -->
    <div v-if="$slots['header-actions']" class="md:hidden fixed bottom-[65px] left-0 right-0 z-50 bg-white/60 backdrop-blur-lg border-t border-gray-100/50 px-5 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
      <div class="flex justify-center [&>button]:w-full [&>button]:justify-center [&>button]:py-3 [&>button]:rounded-xl [&>button]:font-semibold [&>button]:shadow-lg">
        <slot name="header-actions" />
      </div>
    </div>

    <!-- Footer -->
    <div v-if="$slots.footer" class="mt-6 pt-4 border-t border-gray-200">
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup lang="ts">
interface Props {
  title?: string;
  subtitle?: string;
  icon?: string;
  iconColor?: string;
  iconBgColor?: string;
  hoverable?: boolean;
  clickable?: boolean;
  className?: string;
}

// Props réactives
withDefaults(defineProps<Props>(), {
  hoverable: false,
  clickable: false,
  className: ''
});

defineEmits<{
  click: [];
}>();
</script>

<style scoped>
</style>
